<template>
  <div class="license-table" style="height: 100%; width: 100%">
    <!-- Licenses table header -->
    <LicencesTableHeader
      v-model:filter="filter"
      v-model:search-query="searchQuery"
      v-model:active-sub-account-id="activeSubAccountId"
      :license-count="licenseCount || 0"
      :application-licenses="applicationLicenses"
      :selection="selection as LicenceOut[]"
      :application="props.application"
      :show-import-export="props.showImportExport"
      @export-licenses="emit('exportLicenses')"
      @import-licenses="emit('importLicenses')"
      @open-add-user="() => emit('openAddUser')" />

    <div style="height: calc(100% - 50px); position: relative">
      <AccountTable
        v-if="props.application != undefined"
        ref="licenseTable"
        :filter="filter"
        :select="true"
        display-status-indicator
        :display-licence-count="displayLicenseCount"
        :display-phone-number="displayPhoneNumber"
        :application-id="props.application._id"
        display-name
        :only-cached="isSSO"
        :sub-account-id="activeSubAccountId"
        @licenses-changed="emit('licensesChanged')" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { LicenceOut, SoftwareLicense, SoftwareOut } from '@/client'
  import LicenseTable from '@/components/accounts/AccountTable.vue'

  import { useRoute } from 'vue-router'
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { AccountFilters } from '@/stores/account/account.filter'

  const route = useRoute()

  const emit = defineEmits<{
    (e: 'openAddUser'): void
    (e: 'importLicenses'): void
    (e: 'exportLicenses'): void
    (e: 'licensesChanged'): void
  }>()

  const props = defineProps({
    applicationLicenses: {
      type: Array as PropType<SoftwareLicense[]>,
      required: true,
    },
    application: {
      type: Object as PropType<SoftwareOut>,
      required: true,
    },
    showImportExport: {
      type: Boolean,
      default: true,
    },
  })

  const isSSO = computed(() => route.query.sso === 'true')

  const searchQuery = ref<string>('')

  const subAccountId = defineModel<string>('subAccountId')

  const activeSubAccountId = ref<string>(getInitialSubAccountId())

  watch(activeSubAccountId, (newVal) => {
    if (!newVal) {
      subAccountId.value = undefined
    } else {
      const subAccount = props.application?.sub_accounts?.[newVal]
      subAccountId.value = subAccount?.id ?? newVal
    }
    licenseTable.value?.updateLicenses({ allowCached: false })
  })

  watch(
    () => subAccountId.value,
    (newVal) => {
      if (!newVal || newVal === activeSubAccountId.value) return

      activeSubAccountId.value = newVal
    }
  )

  watch(
    () => route.params.subAccountId,
    (param) => {
      if (!isSSO.value) return
      const resolved = Array.isArray(param) ? param[0] : (param as string)
      if (resolved) {
        activeSubAccountId.value = resolved
      }
    }
  )

  const licenseTable = ref<ComponentExposed<typeof LicenseTable>>()

  function refetchSoftwareLicenses() {
    licenseTable.value?.refetchSoftwareLicenses()
  }
  function handleLicencesRefetch({ cached = false } = {}) {
    licenseTable.value?.updateLicenses({ allowCached: cached })
  }

  function getInitialSubAccountId() {
    if (isSSO.value) {
      return route.params.subAccountId as string
    }
    if (subAccountId.value) return subAccountId.value
    if (props.application?.sub_accounts) {
      const firstKey = Object.keys(props.application.sub_accounts)[0]
      return props.application.sub_accounts[firstKey]?.id ?? firstKey
    }
    return ''
  }

  const licenseCount = computed(() => {
    return licenseTable.value?.filteredCount
  })

  const selection = computed(() => {
    return licenseTable.value?.selection
  })

  const licenses = computed(() => {
    return licenseTable.value?.licences
  })

  // Only display the license count for Adobe and Microsoft365
  const displayLicenseCount = computed(() => {
    const applicationName = props.application?.software_name
    if (applicationName == 'Adobe' || applicationName == 'Microsoft365') {
      return true
    }
    return false
  })

  const displayPhoneNumber = computed(() => {
    const applicationName = props.application?.software_name
    if (applicationName == 'ThreeCX') {
      return true
    }
    return false
  })

  // Filter

  const filter = ref<AccountFilters>({})

  watch(
    activeSubAccountId,
    () => {
      if (activeSubAccountId.value) {
        filter.value.subAccountId = activeSubAccountId.value
      } else {
        filter.value.subAccountId = undefined
      }
    },
    { immediate: true }
  )

  watch(
    () => props.application,
    () => {
      if (props.application?._id) {
        filter.value.applicationId = props.application._id
      }
      if (!isSSO.value && props.application?.sub_accounts) {
        const firstKey = Object.keys(props.application.sub_accounts)[0]
        if (firstKey && !activeSubAccountId.value) {
          const fallback = props.application.sub_accounts[firstKey]?.id
          activeSubAccountId.value = fallback ?? firstKey
        }
      }
    },
    { immediate: true }
  )

  defineExpose({
    handleLicencesRefetch,
    licenseCount,
    licenses: licenses,
    accountFilter: filter,
    refetchSoftwareLicenses: refetchSoftwareLicenses,
  })
</script>
